import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { JournalEntryDetail, JournalEntryLine } from '@/types';


interface EntryDisplay {
  id: number;
  reference?: string;
  entry_date: string;
  status: string;
  description?: string;
  lines: JournalEntryLine[];
  total_debits: number;
  total_credits: number;
}

export function Journal() {
  const [entries, setEntries] = useState<EntryDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchEntries() {
      try {
        const data = await api.get<{ items: { id: number; reference?: string; entry_date: string; is_posted: boolean; description?: string }[] }>('/api/journal', { errorCode: ErrorCode.FETCH_JOURNAL, token: idToken! });
        const raw = data.items ?? [];
        const mapped: EntryDisplay[] = raw.map((entry) => {
          const det = entry as unknown as JournalEntryDetail;
          const lines = det.lines ?? [];
          const total_debits = lines.reduce((sum, l) => sum + Number(l.debit), 0);
          const total_credits = lines.reduce((sum, l) => sum + Number(l.credit), 0);
          return {
            id: entry.id,
            reference: entry.reference,
            entry_date: entry.entry_date,
            status: entry.is_posted ? 'posted' : 'draft',
            description: entry.description,
            lines,
            total_debits,
            total_credits,
          };
        });
        setEntries(mapped);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch journal entries');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchEntries();
  }, [retryKey, idToken]);

  const filteredEntries = entries.filter((entry) => {
    if (startDate && entry.entry_date < startDate) return false;
    if (endDate && entry.entry_date > endDate) return false;
    return true;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <i className="bi bi-arrow-repeat text-2xl mb-3 animate-spin"></i>
        <p>Loading journal entries...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Journal Entries</h1>
          <p className="text-sm text-muted-foreground">Double-entry accounting records</p>
        </div>
        <Button variant="default">
          <i className="bi bi-plus mr-2"></i> New Entry
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Start Date</label>
              <input
                type="date"
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">End Date</label>
              <input
                type="date"
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {filteredEntries.length > 0 ? (
        <div className="flex flex-col gap-4">
          {filteredEntries.map((entry) => (
            <Card key={entry.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-mono font-semibold">{entry.reference ?? `JE-${entry.id}`}</span>
                    <span className="text-muted-foreground text-sm">{formatDate(entry.entry_date)}</span>
                  </div>
                  <Badge variant={entry.status === 'posted' ? 'secondary' : 'outline'}>
                    {entry.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {entry.description && (
                  <p className="text-muted-foreground mb-4">{entry.description}</p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2">Account</th>
                        <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2">Debit</th>
                        <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.lines.map((line) => (
                        <tr key={line.id} className="border-b border-border last:border-0">
                          <td className="px-4 py-2">
                            <code className="text-sm">{line.account_code}</code> {line.account_name}
                          </td>
                          <td className="px-4 py-2 text-right font-mono">
                            {Number(line.debit) > 0 ? formatCurrency(Number(line.debit)) : ''}
                          </td>
                          <td className="px-4 py-2 text-right font-mono">
                            {Number(line.credit) > 0 ? formatCurrency(Number(line.credit)) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-2 font-semibold">Total</th>
                        <th className="px-4 py-2 text-right font-mono font-semibold">{formatCurrency(entry.total_debits)}</th>
                        <th className="px-4 py-2 text-right font-mono font-semibold">{formatCurrency(entry.total_credits)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {entry.status === 'draft' && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                    <Button variant="default" size="sm">Post</Button>
                    <Button variant="secondary" size="sm">Edit</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent>
            <div className="text-center py-12">
              <i className="bi bi-journal-text text-4xl text-muted-foreground mb-3"></i>
              <h3 className="text-lg font-semibold text-foreground mb-1">No journal entries</h3>
              <p className="text-muted-foreground mb-4">
                Create your first journal entry to start recording transactions.
              </p>
              <Button variant="default">
                <i className="bi bi-plus mr-2"></i> New Entry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
